import { usePostHog } from 'posthog-js/react';
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import { PhoebeChatOrganizationScopeContext } from '@apps/web/components/phoebe_chat/chat_organization_scope';
import { isRecord } from '@apps/web/components/phoebe_chat/record_utils';
import { type UserQuestionItem } from '@apps/web/components/phoebe_chat/user_question_model';
import { type ToolCardPresentation } from '@apps/web/components/phoebe_chat_view/tool_calls/tool_call_template';
import { type ToolExecutionEntry } from '@apps/web/components/phoebe_chat_view/transcript_types';
import { OrganizationContext } from '@apps/web/context/organization_provider';
import { EVENTS } from '@apps/web/lib/events';

const TRAINING_CONTEXTS = [
    'task_clarification',
    'training_checklist',
    'agency_context',
    'playbook',
] as const;

function trainingContexts(entry: ToolExecutionEntry): Array<string> {
    const args = isRecord(entry.arguments) ? entry.arguments : {};
    const values = [args['training_context']];
    const rawQuestions = args['questions'];
    if (Array.isArray(rawQuestions)) {
        for (const question of rawQuestions) {
            if (isRecord(question)) {
                values.push(question['training_context']);
            }
        }
    }
    return TRAINING_CONTEXTS.filter((context) => values.includes(context));
}

export function useUserQuestionAnalytics({
    entry,
    questions,
    actionable,
    resolved,
    nested,
    presentation,
    showTrainingModeContext,
    trainingModeContextReady,
}: {
    readonly entry: ToolExecutionEntry;
    readonly questions: ReadonlyArray<UserQuestionItem>;
    readonly actionable: boolean;
    readonly resolved: boolean;
    readonly nested: boolean;
    readonly presentation: ToolCardPresentation;
    readonly showTrainingModeContext: boolean;
    readonly trainingModeContextReady: boolean;
}) {
    const posthog = usePostHog();
    const chatOrganizationId = useContext(
        PhoebeChatOrganizationScopeContext,
    )?.organizationId;
    const currentOrganizationId =
        useContext(OrganizationContext)?.organizationId;
    const organizationId = chatOrganizationId ?? currentOrganizationId;
    const capturedShownCallIdsRef = useRef(new Set<string>());
    const properties = useMemo(
        () => ({
            organization_id: organizationId ?? null,
            call_id: entry.callId,
            question_count: questions.length,
            input_types: [...new Set(questions.map((q) => q.inputType))].sort(),
            training_contexts: trainingContexts(entry),
            surface: nested ? 'nested' : 'timeline',
            presentation,
            is_training_mode: showTrainingModeContext,
        }),
        [
            entry,
            nested,
            organizationId,
            presentation,
            questions,
            showTrainingModeContext,
        ],
    );
    const capture = useCallback(
        (event: string, extraProperties?: Record<string, unknown>) => {
            try {
                posthog?.capture(event, {
                    ...properties,
                    ...extraProperties,
                });
            } catch {
                // Product analytics is best-effort.
            }
        },
        [posthog, properties],
    );

    useEffect(() => {
        const callId = entry.callId;
        if (
            !trainingModeContextReady ||
            resolved ||
            !actionable ||
            !callId ||
            capturedShownCallIdsRef.current.has(callId)
        ) {
            return;
        }
        capturedShownCallIdsRef.current.add(callId);
        capture(EVENTS.ASK_USER_QUESTION_CARD_SHOWN);
    }, [actionable, capture, entry.callId, resolved, trainingModeContextReady]);

    const captureSubmitted = useCallback(
        ({
            answeredCount,
            skippedCount,
        }: {
            readonly answeredCount: number;
            readonly skippedCount: number;
        }) => {
            const outcome =
                skippedCount === 0
                    ? 'answered'
                    : answeredCount === 0
                      ? 'fully_skipped'
                      : 'partially_skipped';
            capture(EVENTS.ASK_USER_QUESTION_CARD_SUBMITTED, {
                outcome,
                answered_count: answeredCount,
                skipped_count: skippedCount,
            });
        },
        [capture],
    );

    const captureCancelled = useCallback(() => {
        capture(EVENTS.ASK_USER_QUESTION_CARD_CANCELLED);
    }, [capture]);

    return { captureSubmitted, captureCancelled };
}
